import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { FaCrown, FaUsers, FaCoins, FaStar, FaCheckCircle, FaEdit, FaChartLine } from 'react-icons/fa';
import { subscriptionsAPI, walletAPI } from '../utils/api';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { getAvatarUrl } from '../utils/avatar';

const PRICE_PRESETS = [
  { value: 500, label: '500 PP', desc: '$5/mo equivalent' },
  { value: 1000, label: '1,000 PP', desc: '$10/mo equivalent' },
  { value: 1500, label: '1,500 PP', desc: '$15/mo equivalent' },
  { value: 2000, label: '2,000 PP', desc: '$20/mo equivalent' },
];

const CreatorSetup = () => {
  const [creatorProfile, setCreatorProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [price, setPrice] = useState(1000);
  const [tierName, setTierName] = useState('Standard');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [subscribers, setSubscribers] = useState([]);
  const [mySubs, setMySubs] = useState([]);
  const [activeTab, setActiveTab] = useState('setup');
  const currentUser = JSON.parse(sessionStorage.getItem('user') || '{}');

  const loadData = useCallback(async () => {
    try {
      const [creatorRes, subsRes, mySubsRes] = await Promise.all([
        subscriptionsAPI.getMyCreator(),
        subscriptionsAPI.getSubscribers(),
        subscriptionsAPI.getMy(),
      ]);
      if (creatorRes.data.is_creator) {
        setCreatorProfile(creatorRes.data);
        setPrice(creatorRes.data.subscription_price);
        setTierName(creatorRes.data.tier_name || 'Standard');
        setDescription(creatorRes.data.description || '');
      }
      setSubscribers(subsRes.data);
      setMySubs(mySubsRes.data);
    } catch (err) {
      console.error('Failed to load creator data', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await subscriptionsAPI.setupCreator(price, tierName, description);
      toast.success(res.data.message);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-white"><div className="grid grid-cols-1 md:grid-cols-12"><Sidebar /><main className="col-span-12 md:col-span-10 flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#4A90E2] border-t-transparent rounded-full animate-spin" /></main></div></div>;
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="creator-setup-page">
      <div className="grid grid-cols-1 md:grid-cols-12">
        <Sidebar />
        <main className="col-span-12 md:col-span-10">
          <div className="max-w-3xl mx-auto p-6 md:p-10">
            {/* Header */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center"><FaCrown className="text-white" /></div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Creator Studio</h1>
                  <p className="text-sm text-gray-500">{creatorProfile ? `${creatorProfile.creator_type === 'verified' ? 'Verified' : 'New'} Creator` : <span>Start earning on <span className="font-outfit font-bold">TalkinPoint</span></span>}</p>
                </div>
              </div>
            </motion.div>

            {/* Stats */}
            {creatorProfile && (
              <div className="grid grid-cols-3 gap-4 mb-8">
                {[
                  { label: 'Subscribers', value: creatorProfile.subscriber_count, icon: FaUsers, color: 'text-blue-500' },
                  { label: 'Revenue', value: <span>{(creatorProfile.total_revenue || 0).toLocaleString()} <span className="font-arthaus">PP</span></span>, icon: FaCoins, color: 'text-amber-500' },
                  { label: 'Status', value: creatorProfile.creator_type === 'verified' ? 'Verified' : 'New Creator', icon: FaStar, color: 'text-purple-500' },
                ].map(s => (
                  <div key={s.label} className="bg-white rounded-xl border border-gray-200 p-4">
                    <s.icon className={`${s.color} mb-2`} size={18} />
                    <p className="text-2xl font-bold text-gray-900">{s.value}</p>
                    <p className="text-xs text-gray-500">{s.label}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Tabs */}
            <div className="flex space-x-1 bg-gray-100 p-1 rounded-full mb-8">
              {[
                { key: 'setup', label: creatorProfile ? 'Edit Plan' : 'Setup', icon: FaEdit },
                { key: 'subscribers', label: `Subscribers (${subscribers.length})`, icon: FaUsers },
                { key: 'subscriptions', label: `My Subs (${mySubs.length})`, icon: FaStar },
              ].map(t => (
                <button key={t.key} onClick={() => setActiveTab(t.key)} data-testid={`creator-tab-${t.key}`} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full text-sm font-medium transition-all ${activeTab === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}>
                  <t.icon size={13} /><span>{t.label}</span>
                </button>
              ))}
            </div>

            {/* Setup Tab */}
            {activeTab === 'setup' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white rounded-2xl border border-gray-200 p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-6">{creatorProfile ? 'Update Subscription Plan' : 'Create Your Subscription'}</h2>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">Monthly Price (<span className="font-arthaus">PointPhoria</span>)</label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {PRICE_PRESETS.map(p => (
                        <button key={p.value} onClick={() => setPrice(p.value)} data-testid={`price-${p.value}`} className={`p-3 rounded-xl border-2 text-center transition-all ${price === p.value ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-gray-300'}`}>
                          <p className="font-bold text-gray-900">{p.label}</p>
                          <p className="text-xs text-gray-500">{p.desc}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Tier Name</label>
                    <input type="text" value={tierName} onChange={e => setTierName(e.target.value)} placeholder="e.g., VIP, Inner Circle, Premium" className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-amber-500/50" data-testid="tier-name-input" />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">What subscribers get</label>
                    <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} placeholder="Exclusive posts, behind-the-scenes, early access..." className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-amber-500/50 resize-none" data-testid="creator-description" />
                  </div>

                  <div className="bg-gray-50 rounded-xl p-4 text-sm">
                    <p className="text-gray-600"><strong>Platform fee:</strong> 16% — You receive <strong>{Math.round(price * 84 / 100).toLocaleString()} <span className="font-arthaus">PP</span></strong> per subscriber/month</p>
                  </div>

                  <button onClick={handleSave} disabled={saving} data-testid="save-creator-btn" className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold rounded-xl hover:shadow-lg transition-all disabled:opacity-50">
                    {saving ? 'Saving...' : creatorProfile ? 'Update Plan' : 'Launch Creator Subscription'}
                  </button>
                </div>
              </motion.div>
            )}

            {/* Subscribers Tab */}
            {activeTab === 'subscribers' && (
              <div className="space-y-3">
                {subscribers.length === 0 ? (
                  <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
                    <FaUsers className="text-4xl text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No subscribers yet</p>
                    <p className="text-gray-400 text-sm mt-1">Share your profile to attract subscribers!</p>
                  </div>
                ) : (
                  subscribers.map(s => (
                    <Link key={s.id} to={`/profile/${s.subscriber?.username}`} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3 hover:shadow-md transition-all block">
                      <img src={getAvatarUrl(s.subscriber?.avatar_url, s.subscriber?.full_name)} alt="" className="w-12 h-12 rounded-full object-cover" />
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">{s.subscriber?.full_name}</p>
                        <p className="text-sm text-gray-500">@{s.subscriber?.username}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-amber-600">{s.price_paid} <span className="font-arthaus">PP</span></p>
                        <p className="text-xs text-gray-400">since {new Date(s.started_at).toLocaleDateString()}</p>
                      </div>
                    </Link>
                  ))
                )}
              </div>
            )}

            {/* My Subscriptions Tab */}
            {activeTab === 'subscriptions' && (
              <div className="space-y-3">
                {mySubs.length === 0 ? (
                  <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
                    <FaStar className="text-4xl text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">Not subscribed to any creators</p>
                    <p className="text-gray-400 text-sm mt-1">Find creators to support!</p>
                  </div>
                ) : (
                  mySubs.map(s => (
                    <Link key={s.id} to={`/profile/${s.creator?.username}`} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3 hover:shadow-md transition-all block">
                      <img src={getAvatarUrl(s.creator?.avatar_url, s.creator?.full_name)} alt="" className="w-12 h-12 rounded-full object-cover" />
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">{s.creator?.full_name}</p>
                        <p className="text-sm text-gray-500">@{s.creator?.username}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-400">Expires {new Date(s.expires_at).toLocaleDateString()}</p>
                        <FaCheckCircle className="text-green-500 ml-auto mt-1" size={14} />
                      </div>
                    </Link>
                  ))
                )}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default CreatorSetup;
